import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import stylesPc from "./EditAndUploadToHubModal.module.css";
import stylesSp from "./EditAndUploadToHubModal_SP.module.css";
import styleIpad from "./EditAndUploadToHubModal_IP.module.css";
import { IsDevice } from "../function/useDeviceFlag";
import {
    MenuItem,
    Select,
    createStyles,
    makeStyles,
    Theme,
    Checkbox,
    TextField,
    ThemeProvider,
    createTheme,
} from "@material-ui/core";
import { Group } from "../../user/User";
import { AppState } from "../../root/RootReducer";
import { GetGroupList } from "../../user/UserActions";
import { countCharLength } from "../../utils/ValidationUtils";
import { t } from "i18next";

/**
|------------------------------------------------------------------
|　アップロード・動画編集ダイアログを下記の項目で分割するコンポーネント
|　タイトル、プロフェッショナル、グループ、要約
|------------------------------------------------------------------
*/

interface ModalUpperInputItemsProps {
    isUploadDialog: boolean;
    setIsSaved: (isSaved: boolean) => void;
    // タイトル
    inputVideoName: string;
    InputVideoName: (inputVideoName: string) => void;
    // プロフェッショナル
    inputVideoProfessional: string;
    InputVideoProfessional: (inputVideoProfessional: string) => void;
    // グループ
    group: Group[];
    setGroup: (group: Group[]) => void;
    // 要約
    inputVideoDetail: string;
    InputVideoDetail: (inputVideoDetail: string) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        title: {
            width: IsDevice("iphone") ? "100%" : "440px",
            background: "#FFFFFF0D",
            borderRadius: "5px",
            fontWeight: "bold",
            "& .MuiInputBase-root": {
                height: "40px",
                "& .MuiInputBase-input": {
                    border: "none",
                    color: "white",
                    fontFamily: "Yu Gothic",
                },
            },
        },
        dropdownStyle: {
            color: "white",
            backgroundColor: "var(--textfield-bg)",
        },
        formSelect: {
            border: "1px solid rgba(255, 255, 255, 0.3)",
            width: IsDevice("iphone") ? "100%" : "440px",
            height: "40px",
            fontSize: "14px",
            background: "#FFFFFF0D",
            borderRadius: "5px",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "& .MuiSelect-icon": {
                color: "white",
            },
            "& .MuiSelect-selectMenu": {
                color: "white",
            },
        },
        menuItem: {
            backgroundColor: "var(--textfield-bg)",
            color: "white",
            fontFamily: "noto-sans-cjk-jp,sans-serif !important",
            "&:hover": {
                color: "white",
                backgroundColor: "#35393F",
            },
        },
        textField: {
            width: IsDevice("iphone") ? "100%" : "440px",
            minHeight: IsDevice("iphone") ? "none" : "200px",
            overflowY: "auto",
            background: "#FFFFFF0D",
            border: "none",
            borderRadius: "5px",
            "& .MuiInputBase-root": {
                minHeight: "200px",
                "& .MuiInputBase-input": {
                    height: "163px !important",
                    color: "white",
                    fontFamily: "Yu Gothic",
                },
            },
        },
    })
);

const theme = createTheme({
    palette: {
        secondary: {
            main: "#16ADC1",
        },
    },
});

function ModalUpperInputItems({
    isUploadDialog,
    setIsSaved,
    // タイト
    inputVideoName,
    InputVideoName,
    // プロフェッショナル
    inputVideoProfessional,
    InputVideoProfessional,
    // グループ
    group,
    setGroup,
    // 要約
    inputVideoDetail,
    InputVideoDetail,
}: ModalUpperInputItemsProps) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : IsDevice("ipad") ? styleIpad : stylesSp;
    const classes = useStyles();
    const dispatch = useDispatch();

    // ユーザー取得
    const { professionals, groupList } = useSelector((state: AppState) => ({
        professionals: state.user.userInfoList,
        groupList: state.user.groupList,
    }));

    // グループ一覧
    useEffect(() => {
        if (groupList && groupList.length === 0) {
            dispatch(GetGroupList());
        }
        // eslint-disable-next-line
    }, []);

    const [isErrorTitleVali, ChangeTitleValiFlag] = useState(false);

    const [isErrorDescriptionVali, ChangeDescriptionValiFlag] = useState(false);

    // タイトルを入力する際に実行
    const handleChangeTitle = (event: any) => {
        if (countCharLength(event.target.value) > 80) {
            ChangeTitleValiFlag(true);
        } else {
            ChangeTitleValiFlag(false);
        }
        InputVideoName(event.target.value);
        setIsSaved(false);
    };

    // プロフェッショナル
    const UserInputVideoProfessional = (event: any) => {
        InputVideoProfessional(event.target.value);
        setIsSaved(false);
    };

    // グループ選択
    const UserInputVideoGroup = (event: any) => {
        const groupNames: string[] = event.target.value;
        if (groupNames.includes("選択なし")) groupNames.splice(0, 1);
        const tempGroups = groupList.filter((group) => groupNames.includes(group.title));
        setGroup(tempGroups);
        setIsSaved(false);
    };

    // 要約を入力する際に実行
    const handleChangeDescription = (event: any) => {
        if (countCharLength(event.target.value) > 600) {
            ChangeDescriptionValiFlag(true);
        } else {
            ChangeDescriptionValiFlag(false);
        }
        InputVideoDetail(event.target.value);
        setIsSaved(false);
    };

    return (
        <>
            <ThemeProvider theme={theme}>
                {/* タイトル */}
                <li>
                    <div className={styles.formHelperText}>
                        {t("タイトル")}
                        {isUploadDialog && (
                            <div className={styles.requiredText}> {t("(必須)")}</div>
                        )}
                    </div>
                    <div className={styles.form}>
                        <TextField
                            color="secondary"
                            className={classes.title}
                            id="videoName"
                            variant="outlined"
                            placeholder={String(t("ビデオタイトルを入力"))}
                            autoComplete="off"
                            value={inputVideoName}
                            onChange={handleChangeTitle}
                            error={isErrorTitleVali}
                            helperText={
                                isErrorTitleVali &&
                                t("全角40文字（半角80文字）以内で正しく入力してください")
                            }
                        />
                    </div>
                </li>
                {/* プロフェッショナル */}
                <li>
                    <div className={styles.formHelperText}>
                        {t("プレゼンター")}
                        {isUploadDialog && (
                            <div className={styles.requiredText}> {t("(必須)")}</div>
                        )}
                    </div>
                    <div className={styles.form}>
                        <>
                            <Select
                                required
                                color="secondary"
                                id="demo-simple-select"
                                variant="outlined"
                                onChange={UserInputVideoProfessional}
                                value={inputVideoProfessional}
                                MenuProps={{
                                    classes: {
                                        paper: classes.dropdownStyle,
                                    },
                                    variant: "menu",
                                }}
                                className={classes.formSelect}
                            >
                                <MenuItem value={"-1"}>{t("選択なし")}</MenuItem>
                                {professionals !== undefined &&
                                    professionals.length > 0 &&
                                    professionals.map(
                                        (row) =>
                                            row.roles.includes("PRESENTER") && (
                                                <MenuItem
                                                    key={row.id}
                                                    value={row.id}
                                                    className={classes.menuItem}
                                                    style={{
                                                        color:
                                                            inputVideoProfessional === row.id
                                                                ? "white"
                                                                : "rgba(255, 255, 255, 0.6)",
                                                        backgroundColor:
                                                            inputVideoProfessional === row.id
                                                                ? "#35393F"
                                                                : "",
                                                    }}
                                                >
                                                    {row.fullName}
                                                </MenuItem>
                                            )
                                    )}
                            </Select>
                        </>
                    </div>
                </li>

                {/* グループ（モーダル表示） */}
                <li>
                    <div className={styles.formHelperTextLong}>{t("グループ")}</div>
                    <div className={styles.form}>
                        <>
                            <Select
                                color="secondary"
                                id="group-select"
                                variant="outlined"
                                onChange={UserInputVideoGroup}
                                value={group ? group.map((group) => group.title) : []}
                                multiple
                                displayEmpty
                                renderValue={(selected: any) => {
                                    if (selected.length === 0) {
                                        return <p>{t("選択なし")}</p>;
                                    }

                                    return selected.join(", ");
                                }}
                                MenuProps={{
                                    getContentAnchorEl: null,
                                    anchorOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                    },
                                    transformOrigin: {
                                        vertical: "top",
                                        horizontal: "left",
                                    },
                                    classes: {
                                        paper: classes.dropdownStyle,
                                    },
                                }}
                                className={classes.formSelect}
                            >
                                <MenuItem disabled value="">
                                    <p>{t("選択なし")}</p>
                                </MenuItem>
                                {groupList &&
                                    groupList.map((row) => (
                                        <MenuItem
                                            key={row.id}
                                            value={row.title}
                                            className={classes.menuItem}
                                            style={{
                                                color: group
                                                    ? group.some(
                                                          (group) => group.title === row.title
                                                      )
                                                        ? "#fff"
                                                        : "rgba(255, 255, 255, 0.6)"
                                                    : "rgba(255, 255, 255, 0.6)",
                                                background: group
                                                    ? group.some(
                                                          (group) => group.title === row.title
                                                      )
                                                        ? "#35393F"
                                                        : ""
                                                    : "",
                                            }}
                                        >
                                            <Checkbox
                                                style={{
                                                    color: "var(--white)",
                                                }}
                                                checked={
                                                    group.findIndex(
                                                        (group) => group.id === row.id
                                                    ) >= 0
                                                }
                                            />
                                            {row.title}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </>
                    </div>
                </li>

                {/* 要約 */}
                <li>
                    <div className={styles.formHelperTextLong}>
                        {t("内容")}
                        {isUploadDialog && (
                            <div className={styles.requiredText}> {t("(必須)")}</div>
                        )}
                    </div>
                    <div className={styles.form}>
                        <TextField
                            color="secondary"
                            id="description"
                            onChange={handleChangeDescription}
                            className={classes.textField}
                            multiline
                            minRows={5}
                            autoComplete="off"
                            value={inputVideoDetail}
                            variant="outlined"
                            placeholder={String(t("内容を入力してください"))}
                            error={isErrorDescriptionVali}
                            helperText={
                                isErrorDescriptionVali &&
                                t("全角300文字（半角600文字）以内で正しく入力してください")
                            }
                        />
                    </div>
                </li>
            </ThemeProvider>
        </>
    );
}

export default ModalUpperInputItems;
